import { useEffect, useState } from 'react';
import useClient from 'api/client';
import { useAlert } from 'components/message/AlertContext';
import { useParams } from 'react-router-dom';
import getEnvironmentStack from 'api/Stack/getEnvironmentStack';
import { Stack, StackType } from '../StackDetails';
import getWarehouseStack from 'api/Stack/getWarehouseStack';
import getPipelineStack from 'api/Stack/getPipelineStack';
import getDatasetStack from 'api/Stack/getDatasetStack';
import updateDatasetStack from 'api/Stack/updateDatasetStack';
import updateEnvironmentStack from 'api/Stack/updateEnvironmentStack';

export default function useStack(type: StackType, uri?: string) {
    const { openAlert } = useAlert();
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    const params = useParams<{ uri: string }>();
    const uriToUse = uri ?? params.uri;
    const client = useClient();
    const [stack, setStack] = useState<Stack>({
        stackUri: '',
        stack: '',
        accountid: '',
        region: '',
        status: '',
        stackid: '',
        link: '',
        resources: '',
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                if (uriToUse) {
                    let response;
                    switch (type) {
                        case StackType.dataset:
                            response = await client?.query(
                                getDatasetStack(uriToUse),
                            );
                            break;
                        case StackType.environment:
                            response = await client?.query(
                                getEnvironmentStack(uriToUse),
                            );
                            break;
                        case StackType.warehouse:
                            response = await client?.query(
                                getWarehouseStack(uriToUse),
                            );
                            break;
                        case StackType.pipeline:
                            response = await client?.query(
                                getPipelineStack(uriToUse),
                            );
                            break;
                    }

                    if (!response.errors) {
                        switch (type) {
                            case StackType.dataset:
                                setStack(response.data.getDataset.stack);
                                break;
                            case StackType.environment:
                                setStack(response.data.getEnvironment.stack);
                                break;
                            case StackType.warehouse:
                                setStack(
                                    response.data.getRedshiftCluster.stack,
                                );
                                break;
                            case StackType.pipeline:
                                setStack(response.data.getSqlPipeline.stack);
                                break;
                        }
                    } else {
                        openAlert({
                            message:
                                'Something went wrong... Could not retrieve stack',
                            severity: 'alert',
                        });
                    }
                }
            } finally {
                setLoading(false);
            }
        };

        if (client) {
            fetchData();
        }
    }, [client, uriToUse]);

    const updateStack = async () => {
        setUpdating(true);
        try {
            if (uriToUse) {
                let response;
                switch (type) {
                    case StackType.dataset:
                        response = await client.mutate(
                            updateDatasetStack(uriToUse),
                        );
                        break;
                    case StackType.environment:
                        response = await client.mutate(
                            updateEnvironmentStack(uriToUse),
                        );
                        break;
                    default:
                        response = {
                            errors: [{ message: `StackType unknown: ${type}` }],
                        };
                        break;
                }
                if (!response.errors) {
                    openAlert({
                        message: `CloudFormation stack stack-${stack.stackUri} update started`,
                        severity: 'success',
                    });
                } else {
                    openAlert({
                        severity: 'alert',
                        message: `Something went wrong... Could not update dataset stack. ${response.errors[0]?.message}`,
                    });
                }
            }
        } finally {
            setUpdating(false);
        }
    };

    return {
        stack,
        loading,
        updateStack,
        updating,
    };
}
